import { Component, OnInit } from '@angular/core';
import { SeoService } from '../../shared/seo.service';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface FaqItem {
  q: string;
  a: string;
  open: boolean;
  tags: string[];
}

interface FaqGroup {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  items: FaqItem[];
}

interface Category {
  id: string;
  label: string;
  icon: string;
  count: number;
}

@Component({
  selector: 'app-faq',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.scss']
})
export class FaqComponent implements OnInit {
  constructor(private seoService: SeoService) {}

  searchTerm = '';
  activeCategory = 'all';
  filteredCount = 0;

  readonly categories: Category[] = [
    { id: 'all',      label: 'All Questions', icon: 'fa-solid fa-list',          count: 0 },
    { id: 'icf',      label: 'About ICF',     icon: 'fa-solid fa-award',          count: 0 },
    { id: 'programs', label: 'Our Programs',  icon: 'fa-solid fa-graduation-cap', count: 0 },
    { id: 'platform', label: 'Platform',      icon: 'fa-solid fa-laptop-code',    count: 0 },
    { id: 'business', label: 'Business',      icon: 'fa-solid fa-briefcase',      count: 0 },
    { id: 'payment',  label: 'Pricing',       icon: 'fa-solid fa-credit-card',    count: 0 },
  ];

  readonly allGroups: FaqGroup[] = [
  {
    id: 'icf',
    title: 'About the ICF',
    subtitle: 'Simple answers about what the ICF is, why it matters, and how the coaching path works.',
    icon: 'fa-solid fa-award',
    items: [
      {
        q: 'What is the ICF?',
        a: `<p>The <strong>International Coaching Federation (ICF)</strong> is the main group people look to when they want to know if a coach is trained the right way.</p>
            <p>This matters because many people want to become a coach, but they feel confused. They do not know who to trust. They do not know what path is real. The ICF gives the coaching world a clear standard.</p>
            <p>When you earn an ICF credential, it tells clients and companies that you took coaching seriously and learned real coaching skills.</p>`,
        open: false,
        tags: ['icf']
      },
      {
        q: 'What are ACC, PCC, and MCC?',
        a: `<p>These are the three main ICF credential levels.</p>
            <ul>
              <li><strong>ACC</strong> — This is where many people start.</li>
              <li><strong>PCC</strong> — This is a bigger step and is often the level companies look for.</li>
              <li><strong>MCC</strong> — This is the highest level.</li>
            </ul>
            <p>Most people begin with ACC and grow from there. NEXGEN Executive Coaching Academy Gold helps you move toward ACC. Platinum helps you move toward PCC.</p>`,
        open: false,
        tags: ['icf']
      },
      {
        q: 'Why does the ICF matter? Can I coach without it?',
        a: `<p>Yes, in many places you can coach without an ICF credential.</p>
            <p>But here is the honest answer: if you want people to trust you faster, if you want stronger clients, and if you want more doors to open, the ICF matters.</p>
            <p>It shows that you did not just call yourself a coach. It shows you trained, practiced, and met a known standard.</p>`,
        open: false,
        tags: ['icf']
      },
      {
        q: 'What is the ICF Credentialing Exam?',
        a: `<p>This is the exam the ICF uses to see if you understand how real coaching works.</p>
            <p>It is not just about memorizing words. It is about knowing how to think like a coach and how to respond well in real situations.</p>
            <p>NEXGEN Executive Coaching Academy helps you prepare step by step, not at the last minute.</p>`,
        open: false,
        tags: ['icf']
      },
      {
        q: 'What are the ICF Core Competencies?',
        a: `<p>These are the main coaching skills the ICF wants every coach to learn.</p>
            <p>They include things like coaching with ethics, building trust, listening well, asking strong questions, helping clients grow, and staying present in the conversation.</p>
            <p>At NEXGEN Executive Coaching Academy, these are not just ideas on a page. They become part of how you coach.</p>`,
        open: false,
        tags: ['icf']
      },
      {
        q: 'What is the difference between Level 1 and Level 2?',
        a: `<p>These are two types of ICF-approved training paths.</p>
            <ul>
              <li><strong>Level 1</strong> helps prepare you for ACC.</li>
              <li><strong>Level 2</strong> helps prepare you for PCC.</li>
            </ul>
            <p>NEXGEN Executive Coaching Academy Gold uses a Level 1 path. Platinum uses a Level 2 path.</p>
            <p>This makes the path easier to understand because your training lines up with the credential you want.</p>`,
        open: false,
        tags: ['icf']
      },
      {
        q: 'How do I renew my ICF credential later?',
        a: `<p>ICF credentials are renewed every 3 years.</p>
            <p>That means you keep learning, keep growing, and keep showing that you are active in your work.</p>
            <p>NEXGEN Executive Coaching Academy is built to help you keep growing, not just finish one program and stop.</p>`,
        open: false,
        tags: ['icf']
      }
    ]
  },
  {
    id: 'programs',
    title: 'Our Programs',
    subtitle: 'Simple help choosing the right path for where you are right now.',
    icon: 'fa-solid fa-graduation-cap',
    items: [
      {
        q: 'Which program should I choose: Silver, Gold, or Platinum?',
        a: `<p>Here is the simple version:</p>
            <ul>
              <li><strong>Choose Silver</strong> if you want to start slow and see if coaching is right for you.</li>
              <li><strong>Choose Gold</strong> if you want a real ICF path and want to build a coaching business.</li>
              <li><strong>Choose Platinum</strong> if you want to go farther, grow bigger, and move toward higher-level coaching work.</li>
            </ul>
            <p>Many people start with Gold because it gives them the clearest next step.</p>`,
        open: false,
        tags: ['programs']
      },
      {
        q: 'Can I upgrade later?',
        a: `<p>Yes.</p>
            <p>NEXGEN Executive Coaching Academy is built so you can start where you are and grow when you are ready.</p>
            <p>You do not lose your progress. You keep moving forward.</p>`,
        open: false,
        tags: ['programs']
      },
      {
        q: 'How long do the programs take?',
        a: `<ul>
              <li><strong>Silver</strong> — about 2 to 3 months</li>
              <li><strong>Gold</strong> — about 5 to 6 months for training, then you keep moving toward your coaching hours</li>
              <li><strong>Platinum</strong> — about 6 to 12 months for training, then you keep moving toward your coaching hours</li>
            </ul>
            <p>You do not have to rush. This is made for real people with real lives.</p>`,
        open: false,
        tags: ['programs']
      },
      {
        q: 'What is the Barter Coaching Platform?',
        a: `<p>This helps solve one of the biggest problems new coaches have.</p>
            <p>Many people ask, “How will I get my coaching hours?”</p>
            <p>The Barter Coaching Platform helps students coach each other, practice with real people, and build confidence while logging hours. This means you do not have to figure it all out alone.</p>`,
        open: false,
        tags: ['programs', 'platform']
      },
      {
        q: 'What is Sticky Outcomes?',
        a: `<p>Sticky Outcomes is a book written by Lyssa deHart. It is part of how NEXGEN Executive Coaching Academy teaches coaching.</p>
            <p>It helps coaches move beyond nice conversations and into real change that lasts.</p>
            <p>It gives you a clear way to help clients make progress in real life.</p>`,
        open: false,
        tags: ['programs']
      },
      {
        q: 'What are RaeNotes Developmental Evaluations?',
        a: `<p>This is feedback on your coaching.</p>
            <p>It helps you see what you are doing well, what needs work, and how to improve.</p>
            <p>That matters because many new coaches do not know if they are really coaching well yet. This gives you more clarity and confidence.</p>`,
        open: false,
        tags: ['programs']
      },
      {
        q: 'Do I need coaching experience before I join?',
        a: `<p>No.</p>
            <p>Many people come to NEXGEN Executive Coaching Academy with strong life experience, work experience, and a desire to help people, but they have never been formally trained as a coach.</p>
            <p>You do not need to already have it all figured out. You just need to be ready to learn and grow.</p>`,
        open: false,
        tags: ['programs']
      },
      {
        q: 'When can I start?',
        a: `<p>You can join when you are ready.</p>
            <p>NEXGEN Executive Coaching Academy is built to be flexible, not rigid. This is not about waiting around for the perfect time. It is about starting your next chapter when it works for your life.</p>`,
        open: false,
        tags: ['programs']
      }
    ]
  },
  {
    id: 'platform',
    title: 'The Platform',
    subtitle: 'How the learning platform works and why it is built for real life.',
    icon: 'fa-solid fa-laptop-code',
    items: [
      {
        q: 'What does the platform include?',
        a: `<p>The platform gives you one place for your learning, your live sessions, your tools, and your progress.</p>
            <p>You can watch lessons, move through your training, access resources, and stay connected.</p>
            <p>It is made to keep things simple and easy to follow.</p>`,
        open: false,
        tags: ['platform']
      },
      {
        q: 'How do live sessions work?',
        a: `<p>Live sessions are made for busy adults.</p>
            <p>That means evenings and weekends, not a rigid schedule that only works for a few people.</p>
            <p>If life gets busy, you still have a path forward.</p>`,
        open: false,
        tags: ['platform']
      },
      {
        q: 'What is the NEXGEN Executive Coaching Academy community?',
        a: `<p>Our purpose is to build a remarkable coaching community.</p>
            <p>This means you are not doing this alone. You learn with other people, grow with other people, and build real relationships along the way.</p>
            <p>That matters because becoming a coach can feel lonely when you try to do everything by yourself.</p>`,
        open: false,
        tags: ['platform']
      },
      {
        q: 'Can I use the platform on my phone or tablet?',
        a: `<p>Yes.</p>
            <p>The platform works across devices, so you can learn in the way that fits your life best.</p>`,
        open: false,
        tags: ['platform']
      }
    ]
  },
  {
    id: 'business',
    title: 'Building Your Business',
    subtitle: 'How NEXGEN Executive Coaching Academy helps you become a coach and build a business.',
    icon: 'fa-solid fa-briefcase',
    items: [
      {
        q: 'What does the business-building part actually teach?',
        a: `<p>It teaches you how to build something real.</p>
            <p>You learn how to talk about what you do, who you help, how to attract the right people, how to price your work, and how to build a business that can last.</p>
            <p>This matters because many coach training programs teach coaching, but do not teach how to turn it into income.</p>`,
        open: false,
        tags: ['business']
      },
      {
        q: 'How much can I earn as a coach?',
        a: `<p>There is no one answer for everyone.</p>
            <p>Your income depends on your path, your clients, your effort, and how you build your business.</p>
            <p>What matters is this: NEXGEN Executive Coaching Academy helps you build both coaching skill and business skill, so you are not left guessing.</p>`,
        open: false,
        tags: ['business']
      },
      {
        q: 'Do I need to quit my job to do this?',
        a: `<p>No.</p>
            <p>Many people at NEXGEN Executive Coaching Academy are working professionals.</p>
            <p>They are building something new while still living real life. That is why our model is flexible and built for evenings, weekends, and join-anytime learning.</p>`,
        open: false,
        tags: ['business']
      },
      {
        q: 'What kind of coaching does NEXGEN Executive Coaching Academy focus on?',
        a: `<p>NEXGEN Executive Coaching Academy is built for executive coaching and serious professional coaching.</p>
            <p>But the skills you learn can also help in other coaching spaces.</p>
            <p>The goal is to help you become a strong coach and build a business you believe in.</p>`,
        open: false,
        tags: ['business']
      }
    ]
  },
  {
    id: 'payment',
    title: 'Pricing & Payment',
    subtitle: 'Simple answers about cost, upgrades, and how payment works.',
    icon: 'fa-solid fa-credit-card',
    items: [
      {
        q: 'What does each program cost?',
        a: `<ul>
              <li><strong>Silver</strong> — $9,995</li>
              <li><strong>Gold</strong> — $11,995</li>
              <li><strong>Platinum</strong> — $13,995</li>
            </ul>
            <p>These are the program prices. If you want help choosing the right path, we can help with that too.</p>`,
        open: false,
        tags: ['payment']
      },
      {
        q: 'Is financing available?',
        a: `<p>Yes.</p>
            <p>We know cost matters. Many good people want to grow, but paying all at once can feel heavy.</p>
            <p>That is why payment options are available.</p>`,
        open: false,
        tags: ['payment']
      },
      {
        q: 'If I start with Silver and upgrade later, do I pay all over again?',
        a: `<p>No.</p>
            <p>Your earlier investment still counts. You move forward from where you are.</p>
            <p>NEXGEN Executive Coaching Academy is built as a path, not a reset button.</p>`,
        open: false,
        tags: ['payment']
      },
      {
        q: 'What is Founders 26?',
        a: `<p>Founders 26 is for people who want to start early and be part of the first group building this community.</p>
            <p>It gives people a way to begin sooner, move forward, and carry that progress into the bigger path ahead.</p>`,
        open: false,
        tags: ['payment', 'programs']
      },
      {
        q: 'Is there a refund policy?',
        a: `<p>Yes. Please review the full terms for the complete details.</p>
            <p>If you have questions before you join, ask us. We want people to move forward with clarity, not pressure.</p>`,
        open: false,
        tags: ['payment']
      }
    ]
  }
];

  // Working copy (gets filtered)
  groups: FaqGroup[] = [];

  ngOnInit(): void {
    this.seoService.applySeo({
  title: 'NEXGEN FAQ | ICF, Programs, Platform, Pricing and Coaching Business Questions',
  description: 'Get simple answers about ICF credentials, Gold and Platinum pathways, the NEXGEN platform, barter coaching, pricing, upgrades, and building a coaching business.',
  keywords: 'NEXGEN FAQ, ICF FAQ, ACC PCC FAQ, coaching certification questions, coaching business FAQ, NEXGEN pricing, barter coaching, executive coaching academy FAQ',
  canonicalUrl: 'https://academy.nexgencoaches.com/faq',
  ogTitle: 'NEXGEN FAQ | ICF, Programs, Platform, Pricing and Coaching Business Questions',
  ogDescription: 'Get simple answers about ICF credentials, Gold and Platinum pathways, the NEXGEN platform, barter coaching, pricing, upgrades, and building a coaching business.',
  ogType: 'website',
  twitterTitle: 'NEXGEN FAQ | ICF, Programs, Platform, Pricing and Coaching Business Questions',
  twitterDescription: 'Simple answers about ICF, NEXGEN programs, platform access, pricing, and business-building.',
  robots: 'index, follow',
  schema: [
    {
      '@type': 'WebPage',
      '@id': 'https://academy.nexgencoaches.com/faq',
      'url': 'https://academy.nexgencoaches.com/faq',
      'name': 'NEXGEN FAQ | ICF, Programs, Platform, Pricing and Coaching Business Questions',
      'description': 'Get simple answers about ICF credentials, Gold and Platinum pathways, the NEXGEN platform, barter coaching, pricing, upgrades, and building a coaching business.'
    },
    {
      '@type': 'FAQPage',
      '@id': 'https://academy.nexgencoaches.com/faq#faqpage',
      'url': 'https://academy.nexgencoaches.com/faq',
      'mainEntity': this.allGroups.flatMap(group =>
        group.items.map(item => ({
          '@type': 'Question',
          name: this.stripHtml(item.q),
          acceptedAnswer: {
            '@type': 'Answer',
            text: this.stripHtml(item.a)
          }
        }))
      )
    }
  ]
});

    // this.injectFaqSchema();
    // Deep copy so filtering doesn't mutate readonly source
    this.groups = this.allGroups.map(g => ({
      ...g,
      items: g.items.map(i => ({ ...i }))
    }));
    this.updateCounts();
    this.filteredCount = this.totalVisible();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private injectFaqSchema(): void {
    const mainEntity = this.allGroups.flatMap(group =>
      group.items.map(item => ({
        '@type': 'Question',
        name: this.stripHtml(item.q),
        acceptedAnswer: {
          '@type': 'Answer',
          text: this.stripHtml(item.a)
        }
      }))
    );

    this.seoService.injectSchema({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': 'https://academy.nexgencoaches.com/faq#faqpage',
      url: 'https://academy.nexgencoaches.com/faq',
      mainEntity
    });
  }

  private stripHtml(value: string): string {
    return String(value || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private updateCounts(): void {
    this.categories.forEach(cat => {
      if (cat.id === 'all') {
        cat.count = this.allGroups.reduce((sum, g) => sum + g.items.length, 0);
      } else {
        cat.count = this.allGroups
          .filter(g => g.id === cat.id)
          .reduce((sum, g) => sum + g.items.length, 0);
      }
    });
  }

  setCategory(id: string): void {
    this.activeCategory = id;
    this.searchTerm = '';
    this.groups = this.allGroups.map(g => ({
      ...g,
      items: g.items.map(i => ({ ...i, open: false }))
    }));
    this.filteredCount = this.totalVisible();
  }

  onSearch(): void {
    const q = this.searchTerm.toLowerCase().trim();
    if (!q) {
      this.groups = this.allGroups.map(g => ({
        ...g,
        items: g.items.map(i => ({ ...i, open: false }))
      }));
      this.filteredCount = this.totalVisible();
      return;
    }

    this.groups = this.allGroups.map(g => ({
      ...g,
      items: g.items
        .filter(i => i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q))
        .map(i => ({ ...i, open: false }))
    }));

    this.filteredCount = this.groups.reduce((sum, g) => sum + g.items.length, 0);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.onSearch();
  }

  visibleGroups(): FaqGroup[] {
    if (this.searchTerm) return this.groups;
    if (this.activeCategory === 'all') return this.groups;
    return this.groups.filter(g => g.id === this.activeCategory);
  }

  toggle(item: FaqItem): void {
    item.open = !item.open;
  }

  private totalVisible(): number {
    return this.visibleGroups().reduce((sum, g) => sum + g.items.length, 0);
  }
}